import {
  type IBooleanCondition,
  type IBooleanExpressionCondition,
} from '../../inputs/expressions-editor/conditions/IBooleanCondition.tsx';
import { collectExpressionReferences } from '../../schema/expressions/references/collectExpressionReferences.ts';
import { findIndexReferenceChildOf } from '../../schema/expressions/references/findIndexReferenceChildOf.ts';
import { ReferencePathDirection } from '../../schema/expressions/references/IReferencePath.ts';
import { type ICodeWidget } from '../../schema/widgets/ICodeWidget.ts';
import { type IParentWidgetScope } from '../../schema/widgets/IWidgetType.ts';

/**
 * In caso di widget annidati, è necessario che le referenze all'interno delle
 * condizioni di visibilità e abilitazione siano riferite al widget padre;
 * per questo motivo, è necessario "rimappare" le reference, in modo tale
 * che ad esempio, da `flag` diventino `^.flag`.
 */
export function remapReferencesUpwardInScope(
  parent: ICodeWidget,
  parentScope: IParentWidgetScope,
): IParentWidgetScope {
  return parentScope.map(scope => {
    return {
      ...scope,
      visible:
        scope.visible?.type === 'expression'
          ? remapExpressionReferencesUpwardInScope(parent, scope.visible)
          : scope.visible,
      enabled:
        scope.enabled?.type === 'expression'
          ? remapExpressionReferencesUpwardInScope(parent, scope.enabled)
          : scope.enabled,
    };
  });
}

export function remapExpressionReferencesUpwardInScope(
  parent: ICodeWidget,
  condition: IBooleanExpressionCondition,
) {
  const cloned = structuredClone(condition);
  const references = collectExpressionReferences(cloned.expression);
  for (const reference of references) {
    // Se il padre fornito è già nel `path` della reference, ma come `child`,
    // vuol dire che questa Reference puntava _in basso_
    // ^ (verso il parent) + v (verso il child) si elidono, quindi il risultato
    // è che eliminiamo il path `Child` (ci troviamo allo stesso livello)
    const childIndex = findIndexReferenceChildOf(reference, parent.code);
    if (childIndex >= 0) {
      reference.path.splice(childIndex, 1);
      continue;
    }

    reference.path.unshift({
      direction: ReferencePathDirection.Parent,
      code: parent.code,
    });
  }
  return cloned;
}

export function remapBooleanConditionReferencesUpwardInScope(
  parent: ICodeWidget,
  condition: IBooleanCondition | null | undefined,
) {
  if (!condition || condition.type === 'static') return condition;
  return remapExpressionReferencesUpwardInScope(parent, condition);
}
